// MACHINERY V2 (Stroje) — Fleet V2 row cells (M2.3).
//
// Reference impl: design_handoff_machinery_v3/machinery-v2-grouped.jsx (the
// "aktuálne job" column and the SR chip). Split out of FleetRow.tsx for the
// 300-line cap.
//
// THE LIVE JOB CELL READS PRESENTATION FLAGS ONLY. It never re-tests
// `alarms.length`, `status === "running"` or `conn` — the artboard's own row
// does exactly that, and reproducing it would re-open the split brain the M2.1
// contract closed. The branch order below mirrors the card footer's precedence
// (alarm > program > no-telemetry > status), so a row and a card can never
// describe the same machine differently.
//
// NOTHING IS INVENTED. The gateway summary carries no program, alarm text,
// part count, rpm or since/heartbeat today (fleetTelemetry.ts), so most rows
// resolve to the status branch and simply state the status. That is the honest
// rendering; the richer branches light up unchanged when the deferred backend
// (§6/§7) starts sending the fields.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import useTranslation from "../../../../shared/hooks/useTranslation";
import { Machine } from "../../models/models";
import StatusIcon from "./StatusIcon";
import { MachineryV2CardPresentation, MachineryV2Telemetry, STATUS_SPECS } from "./cardStates";
import { healthColor } from "./fleetCardAtoms";

const Eyebrow: React.FC<{ text: string; tone?: string }> = ({ text, tone }) => (
  <div className="eyebrow" style={{ marginBottom: 2, color: tone }}>
    {text}
  </div>
);

const Muted: React.FC<{ text: string }> = ({ text }) => (
  <div
    style={{
      fontSize: 11.5,
      color: "var(--muted)",
      marginTop: 2,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    }}
  >
    {text}
  </div>
);

export interface FleetRowJobCellProps {
  telemetry: MachineryV2Telemetry;
  presentation: MachineryV2CardPresentation;
}

/** The "aktuálne" column: what this machine is doing right now. */
export const FleetRowJobCell: React.FC<FleetRowJobCellProps> = ({
  telemetry: t,
  presentation: p,
}) => {
  const { translate, getCurrentLanguage } = useTranslation("myMachinery");
  const lang = getCurrentLanguage();

  if (p.showAlarmStrip && t.alarms.length > 0) {
    const alarm = t.alarms[0];
    return (
      <div style={{ minWidth: 0 }}>
        <Eyebrow
          text={translate(
            p.alarmLastKnown
              ? "myMachinery:v2.fleet.alarmLastKnown"
              : "myMachinery:v2.fleet.activeAlarm"
          )}
          tone="var(--red)"
        />
        <div
          style={{
            display: "flex", alignItems: "center", gap: 6, fontSize: 12.5,
            color: "color-mix(in oklab, var(--red) 80%, var(--ink))", fontWeight: 600,
          }}
        >
          <StatusIcon kind="alert" size={12} />
          <span style={{ fontFamily: "var(--font-mono)" }}>{alarm.code}</span>
        </div>
        {alarm.message && <Muted text={alarm.message} />}
      </div>
    );
  }

  if (p.showProgramLine && t.program) {
    const parts = [
      t.partCount != null ? t.partCount.toLocaleString(lang) + " " + translate("myMachinery:v2.pieces") : null,
      t.spindleRpm != null ? t.spindleRpm.toLocaleString(lang) + " RPM" : null,
    ].filter(Boolean);
    return (
      <div style={{ minWidth: 0 }}>
        <Eyebrow text={translate(STATUS_SPECS.running.labelKey)} />
        <div
          style={{
            fontFamily: "var(--font-mono)", fontSize: 12.5, color: "var(--ink-2)",
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
          }}
        >
          <span style={{ color: "var(--green)" }}>{"\u25B6 "}</span>
          {t.program}
          {parts.length > 0 && (
            <span style={{ color: "var(--faint)" }}>{" · " + parts.join(" · ")}</span>
          )}
        </div>
      </div>
    );
  }

  // No connection row at all: the column stays empty rather than explaining
  // itself on every one of the ~99% of rows that look like today's list.
  if (p.showNoTelemetry) {
    return <span style={{ fontSize: 12, color: "var(--faint)" }}>—</span>;
  }

  const label = p.badgeStatus
    ? translate(STATUS_SPECS[p.badgeStatus].labelKey)
    : p.showAwaitingBadge
    ? translate("myMachinery:v2.states.awaiting")
    : translate("myMachinery:v2.states.stale");

  return (
    <div style={{ minWidth: 0 }}>
      <Eyebrow text={label} />
      <div
        style={{
          fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--faint)",
          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
        }}
      >
        {t.heartbeat || t.since || "—"}
      </div>
    </div>
  );
};

/** Open-service-report chip. Colour follows the SAME 0 / 1-2 / 3+ ramp as the
 *  card's health dot (fleetCardAtoms.healthColor) — deliberately NOT the
 *  artboard's status tint, which colours SR pressure by whether the spindle
 *  happens to be turning. One signal, one ramp. */
export const FleetRowSrChip: React.FC<{ machine: Machine }> = ({ machine }) => {
  const { translate } = useTranslation("myMachinery");
  const open = machine.openServiceReportsCount;

  if (!open) {
    return (
      <span style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--faint)" }}>
        —
      </span>
    );
  }

  const color = healthColor(open);
  return (
    <span
      title={translate("myMachinery:healthOpenReports", { count: open })}
      style={{
        display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 9px",
        borderRadius: 999, background: "var(--card)", color,
        border: "1px solid " + color, fontFamily: "var(--font-mono)",
        fontSize: 11, fontWeight: 600, whiteSpace: "nowrap",
      }}
    >
      <span style={{ width: 5, height: 5, borderRadius: 999, background: "currentColor" }} />
      {open} SR
    </span>
  );
};
